import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import { ArrowRight, Building, Home, Warehouse, Shield, Paintbrush } from 'lucide-react';

const Construction = () => {
  const constructionServices = [
    {
      name: 'Warehouse',
      slug: 'warehouse', 
      description: 'Industrial warehouses, logistics centers, and manufacturing facilities with heavy-duty infrastructure.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/ngjyv2tj_Best%20warehouse%20construction%20in%20bangalore.jpg',
      features: ['Pre-Engineered Building', 'Heavy Duty Flooring', 'Loading Facilities', 'Security Systems']
    },
    {
      name: 'Court Structures',
      slug: 'court-structures',
      description: 'Specialized court infrastructure including roofing, lighting systems, fencing, and support structures for all sports courts.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/yczdc8w2_Best%20sports%20infrastructure%20construction%20in%20bangalore.jpg',
      features: ['Court Roofing', 'LED Lighting Systems', 'Boundary Fencing', 'Spectator Stands']
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <Header />
      <FixedActions />
      
      {/* Hero Section */}
      <section style={{ 
        padding: '120px 0 60px', 
        background: 'linear-gradient(135deg, #004534 0%, #006B4A 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200") center/cover',
          opacity: 0.1
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '800px', 
            margin: '0 auto',
            color: 'white'
          }}>
            <h1 style={{ 
              fontSize: '3.5rem', 
              fontWeight: 700, 
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              Excellence in Construction Services
            </h1>
            <p style={{ 
              fontSize: '1.25rem', 
              marginBottom: '40px',
              opacity: 0.9,
              lineHeight: 1.6
            }}>
              From residential homes to commercial complexes and industrial warehouses, 
              we deliver construction projects that stand the test of time with superior quality and craftsmanship.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section style={{ padding: '80px 0', background: '#FAFFEE' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 700, 
              color: 'var(--brand-dark)',
              marginBottom: '16px'
            }}>
              Our Construction Services
            </h2>
            <p style={{ 
              fontSize: '1.125rem', 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Comprehensive construction solutions for residential, commercial, 
              and industrial projects with uncompromising quality standards.
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '40px'
          }}>
            {constructionServices.map((service, index) => (
              <div key={index} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
              }}>
                <div style={{ 
                  width: '100%',
                  aspectRatio: '3/2',
                  maxHeight: '250px',
                  backgroundImage: `url(${service.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    background: 'var(--brand-primary)',
                    color: 'var(--brand-dark)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    Premium Quality
                  </div>
                </div>
                
                <div style={{ padding: '30px' }}>
                  <h3 style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 700, 
                    color: 'var(--brand-dark)',
                    marginBottom: '12px'
                  }}>
                    {service.name}
                  </h3>
                  
                  <p style={{ 
                    color: 'var(--text-secondary)', 
                    lineHeight: 1.6,
                    marginBottom: '20px'
                  }}>
                    {service.description}
                  </p>
                  
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: 600,
                      color: 'var(--brand-dark)',
                      marginBottom: '8px'
                    }}>
                      Key Features:
                    </h4>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '8px'
                    }}>
                      {service.features.map((feature, idx) => (
                        <span key={idx} style={{
                          background: 'rgba(211, 255, 98, 0.2)',
                          color: 'var(--brand-dark)',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <Link 
                    to={`/construction/${service.slug}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'var(--brand-dark)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '25px',
                      textDecoration: 'none',
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#003429';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--brand-dark)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    View Projects <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section style={{ padding: '80px 0', background: 'white' }}>
        <div className="container">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 700, 
                color: 'var(--brand-dark)',
                marginBottom: '24px',
                lineHeight: 1.2
              }}>
                Why Choose Right Shade for Construction?
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {[
                  {
                    title: 'Quality Materials',
                    description: 'We use only the finest materials sourced from trusted suppliers to ensure longevity and durability.'
                  },
                  {
                    title: 'Expert Craftsmanship', 
                    description: 'Our skilled team of engineers and craftsmen bring decades of experience to every project.'
                  },
                  {
                    title: 'Timely Delivery',
                    description: 'We pride ourselves on completing projects on time without compromising on quality standards.'
                  },
                  {
                    title: 'Sustainable Practices',
                    description: 'Environmentally conscious construction methods and materials for a better future.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    gap: '16px',
                    padding: '16px 0'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      background: 'var(--brand-primary)',
                      borderRadius: '50%',
                      marginTop: '8px',
                      flexShrink: 0
                    }} />
                    <div>
                      <h4 style={{ 
                        fontSize: '1.125rem', 
                        fontWeight: 600,
                        color: 'var(--brand-dark)',
                        marginBottom: '4px'
                      }}>
                        {item.title}
                      </h4>
                      <p style={{ 
                        color: 'var(--text-secondary)',
                        lineHeight: 1.6
                      }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ 
              height: '500px',
              backgroundImage: 'url("https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '20px'
            }} />
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        @media (max-width: 1024px) {
          div[style*="grid-template-columns: repeat(auto-fit, minmax(350px, 1fr))"] {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
          
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
        
        @media (max-width: 768px) {
          section {
            padding: 60px 20px !important;
          }
          
          section:first-of-type {
            padding: 100px 20px 60px 20px !important;
          }
          
          section:first-of-type h1 {
            font-size: 2rem !important;
          }
          
          section:first-of-type p {
            font-size: 1rem !important;
          }
          
          .container {
            padding: 0 20px !important;
          }
          
          /* Services section - Remove top padding in mobile */
          section:nth-of-type(2) {
            padding: 40px 20px 60px 20px !important;
          }
          
          /* Stats section */
          section:nth-of-type(2) > div > div {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
          
          /* Services grid */
          section:nth-of-type(3) > div > div {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          
          /* Why choose section image */
          section:nth-of-type(4) > div > div > div:last-child {
            height: 300px !important;
            order: -1;
          }
        }
        
        @media (max-width: 480px) {
          section:first-of-type h1 {
            font-size: 1.75rem !important;
          }
          
          section:nth-of-type(2) > div > div {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <FixedActions />
    </div>
  );
};

export default Construction;